import { useQuery, useInfiniteQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { useAuthStore } from '@/stores/authStore'
import type { StyleId, AspectRatio } from '@/lib/constants'

export interface ReferenceImageInfo {
  s3Key: string
  thumbnailS3Key: string
}

/** 히스토리에 포함되는 템플릿 정보 */
export interface HistoryTemplateInfo {
  id: string
  name: string
  description: string | null
  thumbnailUrl: string | null
  nameI18n?: Record<string, string> | null
  descriptionI18n?: Record<string, string> | null
}

/** 생성된 이미지 정보 (generatedImages 배열의 각 항목) */
export interface GeneratedImageInfo {
  s3Key: string
  seed: number
  imageUrl: string // 백엔드에서 추가되는 프록시 URL
}

/** 생성 히스토리 항목 (1 row = 1 generation request) */
export interface HistoryItem {
  id: string // generationId
  prompt: string // 원본 사용자 프롬프트
  modelId: string
  style: StyleId | null
  aspectRatio: AspectRatio
  referenceImages: ReferenceImageInfo[] | null
  referenceImageUrls: string[] | null
  generatedImages: GeneratedImageInfo[] // 생성된 이미지들
  createdAt: string
  /** 템플릿으로 생성한 경우 템플릿 정보 */
  template: HistoryTemplateInfo | null
}

export interface HistoryResponse {
  items: HistoryItem[]
  pagination: {
    page: number
    limit: number
    total: number
    hasMore: boolean
  }
}

export function useHistory(page: number = 1, limit: number = 20) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const userId = useAuthStore((state) => state.user?.id)

  return useQuery({
    queryKey: ['history', userId, page, limit],
    queryFn: () => api.get<HistoryResponse>(`/history?page=${page}&limit=${limit}`),
    enabled: isAuthenticated && !!userId,
    staleTime: 1000 * 60,
  })
}

export function useInfiniteHistory(limit: number = 10) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const userId = useAuthStore((state) => state.user?.id)

  return useInfiniteQuery({
    queryKey: ['history', 'infinite', userId, limit],
    queryFn: ({ pageParam }) => api.get<HistoryResponse>(`/history?page=${pageParam}&limit=${limit}`),
    getNextPageParam: (lastPage) =>
      lastPage.pagination.hasMore ? lastPage.pagination.page + 1 : undefined,
    initialPageParam: 1,
    enabled: isAuthenticated && !!userId,
    staleTime: 1000 * 60,
  })
}

export function useInvalidateHistory() {
  const queryClient = useQueryClient()
  return () => {
    queryClient.invalidateQueries({ queryKey: ['history'] })
  }
}

/** 생성 히스토리 삭제 (1 generationId = 1 DB row) */
export function useDeleteHistory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/history/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['history'] })
    },
  })
}
